import { TextField } from '@/global/components/TextField'
import { useLocationTestId } from '@/hooks/useTestId'
import localeText from '@/locales/datagrid'
import { useThemeStore } from 'global/components/ToggleTheme'
import React from 'react'
import HorizontalDivider from '../../Divider/HorizontalDivider'
import ColumnEnum from '../utils/columEnum'
import { GridFilterItemPro, GridFilterModelPro } from '../utils/filterModelPro'
import { GridColDefPro } from '../utils/types'
import FilterPanelSelect from './PanelSelect'

export default function FieldValue({
    column,
    onFilterModelChange,
    item,
}: {
    column: GridColDefPro
    item: GridFilterItemPro
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
}) {
    const theme = useThemeStore(state => state.theme)
    const valorFiltroTestId = useLocationTestId('filtros-valor')

    const aplicarValor = (valor: string) =>
        onFilterModelChange(prev => ({
            ...prev,
            items: prev.items.map(p =>
                p.id === item.id ? { ...p, value: valor } : p,
            ),
        }))

    const props = {
        label: localeText.filterPanelValue,
        valorInicial: String(item.value ?? ''),
        'data-testid': valorFiltroTestId,
        onValueChange: aplicarValor,
    }

    return React.useMemo(() => {
        if (!item.operatorValue) return <div className="w-full max-md:hidden" />
        else if (['isEmpty', 'isNotEmpty'].includes(item.operatorValue))
            return <HorizontalDivider className="px-2 max-md:hidden" />
        else if (['SN', 'boolean'].includes(String(column.type)))
            return (
                <SelectValor
                    {...props}
                    options={[
                        {
                            label: localeText.filterPanelValueYes,
                            value: column.type === 'SN' ? 'S' : 'true',
                        },
                        {
                            label: localeText.filterPanelValueNo,
                            value: column.type === 'SN' ? 'N' : 'false',
                        },
                    ]}
                />
            )
        else if (ColumnEnum.isColumnEnum(column.type))
            return <SelectValor {...props} options={column.type.itens} />
        else
            return (
                <TextField
                    label={props.label}
                    variant="outlined"
                    size="small"
                    unHelperText
                    autoComplete="off"
                    type={column.type}
                    defaultValue={props.valorInicial}
                    data-testid={valorFiltroTestId}
                    onChange={event => aplicarValor(event.target.value)}
                />
            )
    }, [item.operatorValue, column.type, theme])
}

/**
 * O `useMemo` acima não observa `item.value` — para não recriar o campo a cada
 * tecla — então o select guarda o próprio valor, como o `defaultValue` já fazia
 * no campo de texto.
 */
function SelectValor({
    valorInicial,
    onValueChange,
    ...props
}: {
    label: string
    options: { value: string; label: React.ReactNode }[]
    valorInicial: string
    className?: string
    'data-testid'?: string
    onValueChange: (valor: string) => void
}) {
    const [valor, setValor] = React.useState(valorInicial)

    return (
        <FilterPanelSelect
            {...props}
            value={valor}
            onValueChange={novo => {
                setValor(novo)
                onValueChange(novo)
            }}
        />
    )
}
